import { useMemo } from "react";
import { SubscriptionStatus } from "../types";
import { isAdminTenantType, isConsoleRole, isFullCapabilityTenantType, isProviderRole, ProviderRole } from "../lib/tenantAccess";

// ── The gate: role × tenant-type × license (locked decisions 2026-07-13) ──
//
// See docs/TENANT_ADMIN_ACCOUNT_UX.md. Everything the gate needs already arrives
// on the single GET /subscription-status call — no new endpoint. This is the
// single source of truth for org/tenant-admin gating; DashboardLayout consumes
// it instead of the old ad-hoc `hasTeam = !!role` / `isOwnerOrAdmin` inlines.
//
// The gate is strictly ADDITIVE: it only decides what extra org-admin surfaces
// to show a qualifying admin. A member, a solo user, and a lapsed admin are all
// "not a tenant admin" and get the individual UX, unchanged.

// The role/tenant-type vocabulary lives in lib/tenantAccess.ts, shared with adminGate — which
// explains a gate this hook closes, so the two must agree by construction rather than by comment.

export interface TenantAdminCapabilities {
  /**
   * Members & seats and the member detail view: roles, aliases, removing people, join requests.
   * Family + business/premium; of a provider's tiers, Admin access only.
   */
  canManageMembers: boolean;
  /**
   * ENG-3407: invite someone, or resend an invitation. The backend needs a MEMBERSHIP in the
   * organization for both (`POST /tenant/invite`, `/resend`: both can add a seat), so a provider's
   * operator, who holds a grant and no membership, is refused at every tier.
   */
  canInviteMembers: boolean;
  /**
   * Groups (ENG-2524). Of a provider's tiers, Admin access only: moving a person between groups is a
   * membership decision (`MspTierEnforcement`). ENG-3749: a family has groups too (Adults, Extra
   * protection), so this is no longer full-tier only.
   */
  canManageGroups: boolean;
  /**
   * ENG-3749: the Google Workspace directory page, which used to ride `canManageGroups`. Split out when
   * family got groups: a household has no Workspace directory. Business/premium exactly as before.
   */
  canUseDirectory: boolean;
  /**
   * ENG-3660: connect or reconnect the Google Workspace directory. A member owner or admin only: the
   * backend refuses a provider at every tier, since the connection would verify the provider's own
   * domain on the client.
   */
  canConnectDirectory: boolean;
  /**
   * ENG-3407: IT provider access — who manages the organization from outside. An owner or admin who
   * is a member only: every `/business/msp/*` write refuses a provider (`requireMemberAdmin`), so a
   * provider cannot re-grant access inside a client.
   */
  canManageManagedAccess: boolean;
  /**
   * ENG-3407: read the organization's group list, for the scope pickers and group filters. Every
   * console role of a full-tier organization — a read needs no tier.
   */
  canViewGroups: boolean;
  /**
   * The organization's own custom sites on Protected sites (`/business/custom-sites`). Business/premium
   * only (family is members-only); of a provider's tiers, Admin access only.
   */
  canManageSites: boolean;
  /**
   * The Extensions page: the allow list, suggested adds and member requests. Business/premium only;
   * of a provider's tiers, Policy only and Admin access.
   */
  canManageWhitelist: boolean;
  /**
   * ENG-3407: the deny list, revoking a member's exception to it, and asking for a code analysis.
   * Not in `MspTierEnforcement`'s policy paths, so a Policy-only provider reads them but cannot
   * change them — nor approve a request for a denied extension, which lifts the deny (`mayLiftDeny`).
   */
  canManageDenyList: boolean;
  /** Admin-mandated locked sites & categories. Business/premium only. */
  canManageLockedSites: boolean;
  /**
   * ENG-3407: site tier and reporting overrides, the organization's and a group's. Beside locked
   * sites on Policy, but not a policy path to the backend, so a Policy-only provider cannot save them.
   */
  canManageSiteOverrides: boolean;
  /** Tenant feature-flag overrides (Business Settings, ENG-2469). Business/premium only. */
  canManageFeatureFlags: boolean;
  /**
   * Organization notification-category defaults (ENG-3059). Business/premium only.
   *
   * Its own capability rather than a reuse of canManageFeatureFlags: they are the same value today
   * (both isFullTier) but not the same question. A labs flag changes what the product DOES for a
   * member; a notification default changes what they are TOLD, and a member can override it. Reusing
   * the flag capability would make separating them later a change to every call site.
   */
  canManageNotifications: boolean;
  /** Monitor-First ⇄ Enforce toggle. Business/premium only. */
  canToggleMonitorFirst: boolean;
  /** Pilot / overview dashboard. Business/premium only. */
  canViewDashboard: boolean;
  /** Browser Risk Report. Business/premium only. */
  canViewRiskReport: boolean;
  /** SaaS/AI Inventory (ENG-3096). Business/premium only. */
  canViewSaasInventory: boolean;
  /**
   * ENG-3407: "What a browser gets" — read-only by construction, so every console role of a full-tier
   * organization, a Read-only provider included.
   */
  canViewProtection: boolean;
  /** A member's activity: last seen, visits, extensions, event tables. Family sees risk only (ENG-3260). */
  canViewMemberActivity: boolean;
  /**
   * ENG-3307: the organization's and its groups' wording and default language (Policy → Language).
   * Not a Policy-only provider's: `/business/language` is not among the writes MspTierEnforcement lets
   * them make, so the control would answer 403. A family's owner and admins get it too: it is protection
   * wording per household and per group, and the Policy page it lives on is theirs since ENG-3749.
   */
  canManageLanguage: boolean;
}

export interface TenantAdmin {
  /**
   * ENG-2534: false while we do not yet KNOW. A failed or in-flight /subscription-status leaves
   * `status` null, which `deriveTenantAdmin` reads as "not an admin" — the same answer it gives a
   * genuine free user. That is why a slow backend made a business admin's whole console vanish with
   * no error: the gate had two states for a three-state question.
   *
   * Consumers must not use this to GRANT anything. It only distinguishes "no" from "not yet", so the
   * nav can wait instead of confidently rendering a smaller app.
   */
  resolved: boolean;
  /** The active tenant's type, verbatim from subscription status (or null). */
  activeTenantType: string | null;
  /** The caller's role in the active tenant (or null). */
  role: string | null;
  /** hasSubscription && status === 'active'. Uniform across all qualifying types. */
  hasActiveLicense: boolean;
  /**
   * The gate: a console role (an owner or admin, or a provider's operator — ENG-3407), of a qualifying
   * tenant type, with an active license. It opens the organization's console; what can be DONE there
   * is `capabilities`.
   */
  isTenantAdmin: boolean;
  /**
   * ENG-3407: the provider tier, when the caller reaches this organization through an IT provider's
   * grant rather than a membership. Null for an owner, admin or member. Billing is never theirs.
   */
  providerTier: ProviderRole | null;
  /** Tenant-type-aware label for the grouped org-admin nav section (null if not admin). */
  groupLabel: string | null;
  capabilities: TenantAdminCapabilities;
}

const NO_CAPABILITIES: TenantAdminCapabilities = {
  canManageMembers: false,
  canInviteMembers: false,
  canManageGroups: false,
  canUseDirectory: false,
  canConnectDirectory: false,
  canManageManagedAccess: false,
  canViewGroups: false,
  canManageSites: false,
  canManageWhitelist: false,
  canManageDenyList: false,
  canManageLockedSites: false,
  canManageSiteOverrides: false,
  canManageFeatureFlags: false,
  canManageNotifications: false,
  canToggleMonitorFirst: false,
  canViewDashboard: false,
  canViewRiskReport: false,
  canViewSaasInventory: false,
  canViewProtection: false,
  canViewMemberActivity: false,
  canManageLanguage: false,
};

// Exported for the tenant switcher (ENG-2387), which labels every row in the list —
// not just the active tenant — so the badge stays consistent across the two surfaces.
export function groupLabelForTenantType(tenantType: string | null): string {
  switch (tenantType) {
    case "family":
      return "Family";
    case "business":
    case "premium":
      return "Business";
    default:
      // MSP / other qualifying types fall back to a generic label.
      return "Organization";
  }
}

/**
 * Pure derivation of the gate from the ACTIVE tenant's subscription status.
 *
 * Written active-tenant-aware from day one: it reads whatever tenant the backend
 * resolved onto `subscriptionStatus`, never "the user's" tenant. A future tenant
 * switch (ENG-2409/2387) is simply *set active tenant server-side → re-fetch
 * /subscription-status → this recomputes* — no switch-specific code needed here.
 *
 * Exported as a plain function so imperative call sites (e.g. App.tsx) and unit
 * tests can use it without a React render; `useTenantAdmin` is the memoized hook.
 */
// Returns everything EXCEPT `resolved`: this is a pure function of the status, and whether a fetch
// has succeeded is a fact about the fetch, not about the status. The hook composes the two.
export function deriveTenantAdmin(
  status: SubscriptionStatus | null | undefined,
): Omit<TenantAdmin, "resolved"> {
  const role = status?.role ?? null;
  const activeTenantType = status?.tenantType ?? null;

  // License axis — uniform: business, premium, AND family all require an active
  // paid subscription (Colin 2026-07-13). A lapsed admin is not a tenant admin.
  const hasActiveLicense = !!status?.hasSubscription && status?.status === "active";

  const providerTier = isProviderRole(role) ? role : null;
  const isQualifyingType = isAdminTenantType(activeTenantType);
  const isTenantAdmin = isConsoleRole(role) && isQualifyingType && hasActiveLicense;

  if (!isTenantAdmin) {
    return {
      activeTenantType,
      role,
      hasActiveLicense,
      isTenantAdmin: false,
      providerTier,
      groupLabel: null,
      capabilities: NO_CAPABILITIES,
    };
  }

  // Tiering: family admins manage members/seats ONLY; business/premium get the
  // full set (protected sites, whitelist, locked sites, Monitor-First, dashboard,
  // risk report). Family is deliberately members-only (Colin, ENG-2408).
  const isFullTier = isFullCapabilityTenantType(activeTenantType);
  // ENG-3749 (Colin, ENG-3240): a family has groups and group policy, but not the company tooling.
  // Groups, and protection policy per group: under protectedModel 1 the Policy page's Sites &
  // categories tab with its scope picker; under 2 the Protected sites editor's group scope (ENG-4063),
  // and Policy's Language tab (ENG-3307) under either. Not enforcement (a family is always Active,
  // ENG-3865), feature flags, notifications, the directory, managed access, the deny list or any reporting.
  const isFamily = activeTenantType === "family";

  // ENG-3407: and a provider's operator gets exactly what their tier lets them do, and nothing the
  // backend would refuse — a control that answers 403 is a broken control. The backend's split
  // (`MspTierEnforcement.allows`): Read only writes nothing; Policy only writes enforcement, feature
  // flags, locked policy, the allow list and member requests, and a group's copies of those; Full
  // admin writes everything an admin can, except what a route refuses every provider (billing,
  // managed access, inviting). Reads are open to every tier.
  const writesPolicy = providerTier !== "msp_read";
  const writesAll = providerTier === null || providerTier === "msp_admin";
  const isMember = providerTier === null;

  const capabilities: TenantAdminCapabilities = {
    canManageMembers: writesAll,
    canInviteMembers: isMember,
    canManageGroups: (isFullTier || isFamily) && writesAll,
    canUseDirectory: isFullTier && writesAll,
    canConnectDirectory: isFullTier && isMember,
    canManageManagedAccess: isFullTier && isMember,
    canViewGroups: isFullTier || isFamily,
    canManageSites: isFullTier && writesAll,
    canManageWhitelist: isFullTier && writesPolicy,
    canManageDenyList: isFullTier && writesAll,
    canManageLockedSites: (isFullTier || isFamily) && writesPolicy,
    canManageSiteOverrides: isFullTier && writesAll,
    canManageFeatureFlags: isFullTier && writesPolicy,
    canManageNotifications: isFullTier && writesAll,
    canToggleMonitorFirst: isFullTier && writesPolicy,
    canViewDashboard: isFullTier,
    canViewRiskReport: isFullTier,
    // ENG-3142: SaaS/AI usage is captured for Business tenants only (isBusinessTenant on both
    // sides), so the page is gated the same way — a premium admin would otherwise read an empty
    // inventory that looks like zero use.
    canViewSaasInventory: activeTenantType === "business",
    canViewProtection: isFullTier,
    canViewMemberActivity: isFullTier,
    canManageLanguage: (isFullTier || isFamily) && writesAll,
  };

  return {
    activeTenantType,
    role,
    hasActiveLicense,
    isTenantAdmin: true,
    providerTier,
    groupLabel: groupLabelForTenantType(activeTenantType),
    capabilities,
  };
}

/**
 * React hook: the single source of gating truth. A pure, memoized derivation of
 * the current subscription status — recomputes for free whenever status changes
 * (e.g. after a tenant switch re-fetches it).
 */
export function useTenantAdmin(
  status: SubscriptionStatus | null | undefined,
  // ENG-2534: has a subscription-status fetch actually SUCCEEDED yet? Defaulted so existing callers
  // and tests keep today's meaning; only the layout passes it.
  resolved = true,
): TenantAdmin {
  return useMemo(() => ({ ...deriveTenantAdmin(status), resolved }), [status, resolved]);
}
